import { easeOut } from "./easing";
import { getSafeAreaTop } from "@/utils/domUtil";

const DEFAULT_SCROLL_MS = 500;

/**
 * スクロール処理の内部実装
 * @param el
 * @param distance
 * @param beforePos
 * @param options
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
const _scroll = (
  el: Window | HTMLElement,
  distance: number,
  beforePos: number,
  duration: number = DEFAULT_SCROLL_MS
) => {
  // 開始時間
  // eslint-disable-next-line no-restricted-syntax
  const start = Date.now();

  // フレームごとに実行する処理
  const frameScroll = () => {
    // eslint-disable-next-line no-restricted-syntax
    const current = Date.now();
    const diff = current - start;
    // 進捗度（移動完了時間までに対して過ぎた時間）
    const progress = diff / duration;
    // 距離 * 進捗度 * イージング関数を移動前の位置に足して、時間ごとの位置に移動する
    const position = beforePos + distance * easeOut(progress);
    el.scrollTo(0, position);
    // 進捗度が1以下なら再帰呼び出し
    if (progress <= 1) {
      requestAnimationFrame(frameScroll);
    }
  };

  // 初回呼び出し
  requestAnimationFrame(frameScroll);
};

/**
 * 任意のtargetElementの位置までwindowかElementをスムーススクロールさせる
 * Elementをスクロールさせる場合は、targetElementは子要素を指定する。
 * optionsで位置調整、durationで移動スピードを指定可能
 * @param scrollElement
 * @param targetElement
 * @param options
 */
export const scrollTo = (
  scrollElement: HTMLElement | Window,
  targetElement: HTMLElement,
  options?: {
    offset?: number;
    duration?: number; // ミリ秒単位
  }
) => {
  // スクロールしたい距離を算出
  let offsetY = 0;
  let targetY = 0;
  if (scrollElement instanceof Window) {
    // Windowの場合は現在のスクロール量が基準
    offsetY = window.pageYOffset;
    targetY = targetElement.offsetTop;
  } else {
    // エレメントをスクロールさせる場合は、対象のオフセットから親（スクロール対象）のオフセットを引いておく
    offsetY = scrollElement.scrollTop;
    targetY = targetElement.offsetTop - scrollElement.offsetTop;
  }
  // offsetが指定されている場合は加算
  if (options && options.offset) {
    targetY += options.offset;
  }
  // ステータスバー等の高さ
  const safeAreaTop = getSafeAreaTop();

  const distance = targetY - offsetY - safeAreaTop;

  // スクロール処理
  const duration = options ? options.duration : undefined;
  _scroll(scrollElement, distance, offsetY, duration);
};

/**
 * 一番上までwindowかElementスムーススクロールさせる
 * optionsで位置調整、durationで移動スピードを指定可能
 * @param scrollElement
 * @param options
 */
export const scrollToTop = (
  scrollElement: HTMLElement | Window,
  options?: {
    offset?: number;
    duration?: number; // ミリ秒単位
  }
) => {
  // トップまでの距離を算出
  let offsetY = 0;
  if (scrollElement instanceof Window) {
    offsetY = window.pageYOffset;
  } else {
    offsetY = scrollElement.scrollTop;
  }

  let distance = -offsetY;
  // offsetが指定されている場合は加算
  if (options && options.offset) {
    distance += options.offset;
  }
  // ステータスバー等の高さ
  const safeAreaTop = getSafeAreaTop();
  distance -= safeAreaTop;

  // スクロール処理
  const duration = options ? options.duration : undefined;
  _scroll(scrollElement, distance, offsetY, duration);
};

/**
 * 一番下までwindowかElementスムーススクロールさせる
 * optionsで位置調整、durationで移動スピードを指定可能
 * @param scrollElement
 * @param options
 */
export const scrollToBottom = (
  scrollElement: HTMLElement | Window,
  options?: {
    offset?: number;
    duration?: number; // ミリ秒単位
  }
) => {
  // ボトムまでの距離を算出
  let offsetY = 0;
  let bottom = 0;
  if (scrollElement instanceof Window) {
    offsetY = window.pageYOffset;
    bottom = document.body.offsetHeight - window.innerHeight;
  } else {
    offsetY = scrollElement.scrollTop;
    bottom = scrollElement.scrollHeight - scrollElement.offsetHeight;
  }
  // offsetが指定されている場合は加算
  if (options && options.offset) {
    bottom += options.offset;
  }
  // ステータスバー等の高さ
  const safeAreaTop = getSafeAreaTop();

  const distance = bottom - offsetY - safeAreaTop;

  // スクロール処理
  const duration = options ? options.duration : undefined;
  _scroll(scrollElement, distance, offsetY, duration);
};
